import { useMemo } from 'react';

// MUI
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Container,
  Stack,
  Typography,
  Divider
} from '@mui/material';

// Mantis
import MainCard from 'components/MainCard';

// Icons
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// ✅ put your logo where you keep images in Mantis
// Example: src/assets/images/freedom/Freedom-ForEver-Logo.jpg
import FreedomLogo from '../../../public/assets/img/Freedom-ForEver-Logo.jpg';
import VPLogo from '../../../public/assets/img/logo.png';


const Section = ({ title, children, defaultExpanded = false }) => (
  <Accordion
    defaultExpanded={defaultExpanded}
    disableGutters
    sx={{
      borderRadius: 2,
      '&:before': { display: 'none' },
      mb: 1.5,
      overflow: 'hidden',
      border: '1px solid',
      borderColor: 'divider'
    }}
  >
    <AccordionSummary
      expandIcon={<ExpandMoreIcon />}
      sx={{
        bgcolor: 'success.lighter',
        '& .MuiAccordionSummary-content': { my: 1 },
        px: 2
      }}
    >
      <Typography variant="h6" sx={{ color: 'success.darker', fontWeight: 700 }}>
        {title}
      </Typography>
    </AccordionSummary>
    <AccordionDetails sx={{ px: 2, py: 2 }}>
      <Typography variant="body1" sx={{ whiteSpace: 'pre-line', lineHeight: 1.75 }}>
        {children}
      </Typography>
    </AccordionDetails>
  </Accordion>
);

export default function FreedomForEver() {
  const stepsText = useMemo(
    () => [
      'BEGIN HERE: https://www.vprous.com',
      '',
      'Step 1: Select “PIN” from the navigation pane',
      'Step 2: Enter Company Code: 202501',
      'Step 3: Enter your Master PIN (last 4 of SSN)',
      'Step 4: Enter a Temp PIN',
      'Step 5: Set the Expiration Date (recommended: at least 30 days out)',
      'Step 6: Check applicable boxes, then select “Update PIN”'
    ].join('\n'),
    []
  );

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <MainCard contentSX={{ p: { xs: 2, sm: 3 } }}>
        <Stack spacing={2}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" sx={{ fontWeight: 800 }}>
              Freedom Forever Employees
            </Typography>
            <Typography variant="body1" sx={{ mt: 1, opacity: 0.85 }}>
              Employment & Income Verification Instructions (Verification Professional)
            </Typography>

            <Box
              component="img"
              src={FreedomLogo}
              alt="Freedom Forever Logo"
              sx={{
                mt: 2,
                width: 190,
                maxWidth: '70%',
                borderRadius: 2,
                boxShadow: 3
              }}
            />
          </Box>

          <Divider />

          <Section title="Introduction" defaultExpanded>
            Dear Freedom Forever Employee,{'\n'}
            {'\n'}
            Freedom Forever has outsourced the employment and income verification process to Verification Professional.
            This overview explains Verification of Employment (VOE) and Verification of Income (VOI), and which entities
            may request this information.
          </Section>

          <Section title="Verification of Employment (VOE)">
            Verification of Employment confirms an individual's current or past employment status. This typically
            includes job title, employment start/end dates (if applicable), and current status (full-time, part-time,
            active, or terminated).
          </Section>

          <Section title="Verification of Income (VOI)">
            Verification of Income is often required for mortgages, rentals, loans, benefit eligibility, and background
            checks. VOI helps confirm that reported income is accurate and aligns with an individual's claims.
          </Section>

          <Section title="Private Verifiers">
            Verification of employment and income is commonly required by mortgage lenders, property managers, auto
            lenders, other loan providers, and prospective employers.{'\n'}
            {'\n'}
            Verification Professional works to securely and efficiently provide authorized details.
          </Section>

          <Section title="Government Verifiers">
            Government entities that commonly request access include:{'\n'}
            - State workforce agencies{'\n'}
            - Social Security Administration{'\n'}
            - Internal Revenue Service{'\n'}
            - Department of Health and Human Services{'\n'}
            - Housing authorities{'\n'}
            - U.S. Citizenship and Immigration Services
          </Section>

          <Section title="Temporary PIN & Expiration Date">
            The Temp PIN acts as secured permission for verifiers requesting income information.{'\n'}
            {'\n'}
            The Expiration Date is the final day the requester can access your information. Recommended: at least 30
            days from creation.
          </Section>

          <Section title="Setting the Temp PIN">{stepsText}</Section>

          <Section title="Info to Provide to the Verifier">
            After your PIN is updated, provide the verifier:{'\n'}
            - Company Code: 202501{'\n'}
            - Social Security number{'\n'}
            - Temp PIN and Expiration Date{'\n'}
            {'\n'}
            Verification Professional Contact:{'\n'}
            info@verificationprofessional.com | (910) 833-5601
          </Section>

          <Section title="Why the Verifier Must Contact Us">
            In accordance with the Fair Credit Reporting Act (FCRA), Verification Professional works directly with
            verifiers to help protect your personal information and support accuracy of reported data.
          </Section>

          <Section title="Need Help?">
            Our office is available Monday–Friday, 9:00 AM to 5:00 PM (ET).{'\n'}
            📞 (910) 833-5601{'\n'}
            ✉ info@verificationprofessional.com
          </Section>
        </Stack>
      </MainCard>
    </Container>
  );
}
